import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight, Clock, Heart, Home, Diamond, Truck, ShieldCheck, Users } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    setIsLoaded(true);
  }, []);
  const scrollToContact = () => {
    const element = document.getElementById('call-to-action');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50">
      {/* Background pattern using pattern guidelines */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 right-0 h-full w-full bg-repeat" style={{
        backgroundImage: `url('https://flowcare.health/lovable-uploads/000cfbcc-abc0-4c33-8279-70b0622a206c.png')`,
        backgroundSize: '200px',
        opacity: 0.08
      }} />
      </div>
      
      {/* Background blur element */}
      <div className="absolute top-1/4 -left-48 w-96 h-96 bg-abbella-aqua/20 rounded-full blur-3xl opacity-70" />
      <div className="absolute bottom-0 -right-48 w-96 h-96 bg-abbella-aqua/10 rounded-full blur-3xl opacity-60" />
      
      <div className="container mx-auto px-6 md:px-12 pt-24 pb-8 md:pt-32 md:pb-12 flex flex-col-reverse md:flex-row items-center justify-between gap-12 lg:gap-16">
        {/* Content Section */}
        <div className="w-full md:w-1/2 z-10">
          {/* Pill/Chip */}
          <div className={`inline-flex items-center gap-2 bg-abbella-aqua/10 backdrop-blur-sm text-abbella-aqua px-4 py-1.5 rounded-full mb-6 border border-abbella-aqua/20 transform transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '200ms'
        }}>
            <Home size={16} strokeWidth={1.5} className="text-abbella-aqua" />
            <span className="text-sm font-medium">FlowCare by Abbella</span>
          </div>
          
          {/* Main Heading */}
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-abbella-ebony leading-tight text-balance text-shadow mb-6 transform transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '400ms'
        }}>Exceptional care that feels like family</h1>
          
          {/* Subheading */}
          <p className={`text-lg sm:text-xl text-slate-700 mb-8 max-w-xl leading-relaxed text-balance transform transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '600ms'
        }}>
            Experience our flexible, no-minimum care model designed around your unique needs. Compassionate care when you need it, where you need it.
          </p>
          
          {/* Hospital-grade staffing highlight */}
          <div className={`flex items-center gap-3 p-3 bg-white/70 backdrop-blur-sm rounded-lg border border-abbella-aqua/20 mb-8 shadow-sm transform transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '650ms'
        }}>
            <div className="p-2 bg-abbella-aqua/10 rounded-full">
              <ShieldCheck size={20} className="text-abbella-aqua" strokeWidth={1.5} />
            </div>
            <p className="text-sm md:text-base font-medium text-abbella-ebony">
              Hospital-grade staffing model that resolves reliability and caregiver shortage issues
            </p>
          </div>
          
          {/* Service Icons */}
          <div className={`flex gap-6 mb-8 transform transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '700ms'
        }}>
            {[{
            icon: <Heart size={24} className="text-abbella-aqua" strokeWidth={1.5} />,
            label: "Compassionate"
          }, {
            icon: <Clock size={24} className="text-abbella-aqua" strokeWidth={1.5} />,
            label: "Flexible"
          }, {
            icon: <Diamond size={24} className="text-abbella-aqua" strokeWidth={1.5} />,
            label: "Quality"
          }, {
            icon: <Truck size={24} className="text-abbella-aqua" strokeWidth={1.5} />,
            label: "Reliable"
          }].map((item, index) => <div key={index} className="flex flex-col items-center">
                <div className="p-3 bg-white rounded-full mb-2 shadow-sm border border-abbella-aqua/10">
                  {item.icon}
                </div>
                <span className="text-sm text-slate-600">{item.label}</span>
              </div>)}
          </div>
          
          {/* CTA Button - Made bigger but removed the pulsating animation */}
          <div className={`transform transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '800ms'
        }}>
            <Button onClick={scrollToContact} className="group bg-abbella-aqua hover:bg-abbella-aqua/90 text-white px-10 py-7 rounded-lg text-xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 focus:ring-2 focus:ring-abbella-aqua/50 focus:ring-offset-2">
              Schedule your free assessment
              <ChevronRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </div>
          
          {/* Social Proof - Updated with slightly larger image */}
          <div className={`flex items-center mt-6 transform transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '900ms'
        }}>
            <div className="relative h-11 mr-3">
              <img src="/lovable-uploads/89d6025e-e53f-4378-aca2-6db66d813e3a.png" alt="FlowCare Community Members" className="h-full w-auto object-contain" />
            </div>
            <div className="text-sm text-slate-600">
              <span className="font-medium">56 families</span> started using our services this month
            </div>
          </div>
        </div>
        
        {/* Image Section */}
        <div className={`w-full md:w-1/2 z-10 transform transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0 translate-y-8'}`} style={{
        transitionDelay: '300ms'
      }}>
          <div className="relative">
            {/* Decorative elements styled like the brand icons */}
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full border-2 border-abbella-aqua/30 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full border-2 border-abbella-aqua/20"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full border-2 border-abbella-aqua/30 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full border-2 border-abbella-aqua/20"></div>
            </div>
            
            {/* Image with glass card effect */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-abbella-aqua/10">
              <div className="absolute inset-0 bg-gradient-to-t from-abbella-ebony/30 to-transparent z-10"></div>
              <img src="/lovable-uploads/d206817d-a5c2-4baa-9f7b-6a65f55ad358.png" alt="Elderly woman and daughter sharing a warm, joyful moment" className="w-full h-auto object-cover transform transition-transform duration-700 hover:scale-105" style={{
              aspectRatio: "4/3"
            }} loading="lazy" />
              
              {/* Glass card overlay with brand-style icon */}
              <div className="absolute bottom-0 left-0 right-0 glass p-6 m-4 rounded-xl z-20 border border-abbella-aqua/20">
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full mr-3 border border-green-600 animate-pulse">
                    <Heart size={16} className="text-white" fill="white" strokeWidth={0} />
                  </div>
                  <p className="text-sm text-abbella-ebony font-medium">Currently serving Baltimore county and surrounding counties</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Hero;